import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtBR, fmtMoney } from '../lib/format';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';

interface GuiaReport {
  competencia: string;
  imposto: string;
  guia_id: number;
  valor_guia: number;
  vencimento: string | null;
  status: string;
  pago_em: string | null;
  retencoes: number;
  detalhe: Array<{ conta_id: number; fornecedor: string; valor: number; nf: string | null }>;
}

function compLabel(c: string): string {
  const m = c.match(/^(\d{4})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : c;
}

export function Guias(_: PageProps) {
  const toast = useToast();
  const rep = useFetch(() => api.relatorio<{ items: GuiaReport[] }>('retencoes'), []);

  const pagar = async (g: GuiaReport) => {
    try {
      await api.pagarConta(g.guia_id, { pago_em: new Date().toISOString().slice(0, 10) });
      toast(`Guia ${g.imposto} ${compLabel(g.competencia)} paga.`);
      rep.refetch();
    } catch { toast('Falha ao pagar guia.', 'err'); }
  };

  const guias = rep.data?.items ?? [];

  return (
    <div className="page mid">
      <h1>Guias de Impostos</h1>
      <div className="subtitle">Consolidadas automaticamente a partir das retenções das notas. Recalculam sozinhas quando retenções mudam.</div>

      {rep.loading ? (
        <div className="loading">Carregando…</div>
      ) : guias.length === 0 ? (
        <div className="empty">Nenhuma guia em aberto — não há retenções na competência atual.</div>
      ) : (
        <div className="grid-2">
          {guias.map((g) => {
            const paga = g.status === 'pago';
            return (
              <div className="card pad-lg" key={g.guia_id}>
                <div className="row" style={{ gap: 10 }}>
                  <span className="tag-tax" style={{ fontSize: 13, padding: '5px 12px' }}>{g.imposto}</span>
                  <span className="text-sm" style={{ color: 'var(--muted-2)' }}>competência {compLabel(g.competencia)}</span>
                  <span className={`badge ${paga ? 'pago' : 'pendente'}`} style={{ marginLeft: 'auto' }}>{paga ? 'Paga' : 'Em aberto'}</span>
                </div>
                <div className="row" style={{ alignItems: 'baseline', gap: 14, margin: '14px 0 4px' }}>
                  <span className="mono" style={{ fontSize: 24, fontWeight: 600, color: 'var(--ink)' }}>{fmtMoney(g.valor_guia)}</span>
                  <span className="text-sm muted">vence {fmtBR(g.vencimento)}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--border-soft)', marginTop: 12, paddingTop: 10 }}>
                  <div className="k-label" style={{ marginBottom: 6 }}>Retenções consolidadas</div>
                  {g.detalhe.map((n, i) => (
                    <div className="row" key={i} style={{ gap: 10, fontSize: 12.5, padding: '4px 0' }}>
                      <span style={{ flex: 1 }} className="ellipsis">{n.fornecedor}{n.nf ? ` · NF ${n.nf}` : ''}</span>
                      <span className="mono">{fmtMoney(n.valor)}</span>
                    </div>
                  ))}
                </div>
                {!paga && <button className="btn primary sm" style={{ marginTop: 12 }} onClick={() => pagar(g)}>Pagar guia</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
